import { useState, type FormEvent } from 'react';
import type { Goal, GoalPriority, GoalStatus } from '@otakotakdev/shared';
import {
  useGoals,
  useCreateGoal,
  useUpdateGoal,
  useDeleteGoal,
} from '@/hooks/api';
import { Badge, EmptyState, ErrorState, ListSkeleton, Modal, PageHeader, Spinner } from '@/components/ui';

const STATUS_OPTIONS: GoalStatus[] = ['inbox', 'active', 'completed', 'cancelled', 'archived'];
const PRIORITY_OPTIONS: GoalPriority[] = ['low', 'medium', 'high'];

interface GoalFormState {
  title: string;
  description: string;
  priority: GoalPriority;
  targetDate: string;
  status: GoalStatus;
}

const emptyForm: GoalFormState = {
  title: '',
  description: '',
  priority: 'medium',
  targetDate: '',
  status: 'active',
};

function GoalForm({
  open,
  initial,
  onClose,
  onSaved,
}: {
  open: boolean;
  initial: Goal | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<GoalFormState>(
    initial
      ? {
          title: initial.title,
          description: initial.description,
          priority: initial.priority,
          targetDate: initial.targetDate ?? '',
          status: initial.status,
        }
      : emptyForm,
  );
  const create = useCreateGoal();
  const update = useUpdateGoal();
  const mutation = initial ? update : create;
  const error = mutation.error;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    const payload = {
      title: form.title.trim(),
      description: form.description,
      priority: form.priority,
      targetDate: form.targetDate || null,
      ...(initial ? {} : { status: form.status, source: 'dashboard' }),
    };
    if (initial) {
      update.mutate(
        { id: initial.id, ...payload, status: form.status },
        { onSuccess: onSaved },
      );
    } else {
      create.mutate(payload, { onSuccess: onSaved });
    }
  };

  return (
    <Modal open={open} title={initial ? 'Edit goal' : 'New goal'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="goal-title" className="label">
            Title
          </label>
          <input
            id="goal-title"
            className="input"
            required
            maxLength={200}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </div>
        <div>
          <label htmlFor="goal-desc" className="label">
            Description
          </label>
          <textarea
            id="goal-desc"
            className="input min-h-24"
            maxLength={4000}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="goal-priority" className="label">
              Priority
            </label>
            <select
              id="goal-priority"
              className="input"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value as GoalPriority })}
            >
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="goal-date" className="label">
              Target date
            </label>
            <input
              id="goal-date"
              type="date"
              className="input"
              value={form.targetDate}
              onChange={(e) => setForm({ ...form, targetDate: e.target.value })}
            />
          </div>
        </div>
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error instanceof Error ? error.message : 'Failed to save goal'}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={mutation.isPending || !form.title.trim()}>
            {mutation.isPending ? <Spinner label="Saving…" /> : initial ? 'Save changes' : 'Create goal'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function GoalCard({
  goal,
  onEdit,
  onComplete,
  onArchive,
  onDelete,
}: {
  goal: Goal;
  onEdit: () => void;
  onComplete: () => void;
  onArchive: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="card flex flex-col gap-2 p-4">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-medium">{goal.title}</h3>
        <Badge
          tone={
            goal.status === 'completed'
              ? 'success'
              : goal.priority === 'high'
                ? 'danger'
                : goal.priority === 'medium'
                  ? 'warning'
                  : 'default'
          }
        >
          {goal.status}
        </Badge>
      </div>
      {goal.description && <p className="line-clamp-2 text-sm text-muted">{goal.description}</p>}
      <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-2">
        <Badge>{goal.priority}</Badge>
        {goal.targetDate && <Badge>due {goal.targetDate}</Badge>}
        {goal.completedAt && <Badge tone="success">done {goal.completedAt.slice(0, 10)}</Badge>}
        <div className="ml-auto flex gap-1.5">
          {goal.status !== 'completed' && (
            <button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={onComplete}>
              Complete
            </button>
          )}
          {goal.status !== 'archived' && (
            <button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={onArchive}>
              Archive
            </button>
          )}
          <button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={onEdit}>
            Edit
          </button>
          <button type="button" className="btn-danger px-2 py-1 text-xs" onClick={onDelete}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export function GoalsPage() {
  const [statusFilter, setStatusFilter] = useState<GoalStatus | ''>('active');
  const [priorityFilter, setPriorityFilter] = useState<GoalPriority | ''>('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Goal | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Goal | null>(null);

  const params: Record<string, unknown> = { limit: 100 };
  if (statusFilter) params.status = statusFilter;
  if (priorityFilter) params.priority = priorityFilter;

  const { data, isLoading, isError, error, refetch } = useGoals(params);
  const update = useUpdateGoal();
  const remove = useDeleteGoal();

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (goal: Goal) => {
    setEditing(goal);
    setFormOpen(true);
  };

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="Goals"
        subtitle="Track what matters"
        actions={
          <button type="button" className="btn-primary" onClick={openCreate}>
            + New goal
          </button>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <select
          className="input w-auto"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as GoalStatus | '')}
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          className="input w-auto"
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value as GoalPriority | '')}
          aria-label="Filter by priority"
        >
          <option value="">All priorities</option>
          {PRIORITY_OPTIONS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      {isLoading && <ListSkeleton rows={4} />}
      {isError && (
        <ErrorState
          message={error instanceof Error ? error.message : 'Failed to load goals'}
          onRetry={() => void refetch()}
        />
      )}
      {data && data.items.length === 0 && (
        <EmptyState
          title="No goals yet"
          hint="Create your first goal to start tracking progress."
          action={
            <button type="button" className="btn-primary mt-2" onClick={openCreate}>
              Create goal
            </button>
          }
        />
      )}
      {data && data.items.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2">
          {data.items.map((goal) => (
            <GoalCard
              key={goal.id}
              goal={goal}
              onEdit={() => openEdit(goal)}
              onComplete={() => update.mutate({ id: goal.id, status: 'completed' })}
              onArchive={() => update.mutate({ id: goal.id, status: 'archived' })}
              onDelete={() => setConfirmDelete(goal)}
            />
          ))}
        </div>
      )}

      {formOpen && (
        <GoalForm
          key={editing?.id ?? 'new'}
          open={formOpen}
          initial={editing}
          onClose={() => setFormOpen(false)}
          onSaved={() => setFormOpen(false)}
        />
      )}

      <Modal
        open={confirmDelete !== null}
        title="Delete goal?"
        onClose={() => setConfirmDelete(null)}
      >
        <p className="mb-4 text-sm text-muted">
          This permanently deletes <span className="text-ink">{confirmDelete?.title}</span>. Related
          activities will be unlinked but kept.
        </p>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={() => setConfirmDelete(null)}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-danger"
            disabled={remove.isPending}
            onClick={() => {
              if (!confirmDelete) return;
              remove.mutate(confirmDelete.id, {
                onSuccess: () => setConfirmDelete(null),
              });
            }}
          >
            Delete
          </button>
        </div>
      </Modal>
    </div>
  );
}
